import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { AuthConfig, User } from "../types";
import { keys } from "./keys";

// ---------------------------------------------------------------- auth

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: () => api.get<User>("/auth/me"), retry: false });

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string }) =>
      api.post<User>("/auth/login", body),
    onSuccess: (user) => qc.setQueryData(keys.me, user),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSuccess: () => {
      qc.clear();
      qc.setQueryData(keys.me, null);
    },
  });
}

/** What the login screen offers ("Continuar con Google" once the OAuth client exists). */
export const useAuthConfig = () =>
  useQuery({
    queryKey: keys.authConfig,
    queryFn: () => api.get<AuthConfig>("/auth/config"),
    staleTime: Infinity,
    retry: false,
  });

/** Password sign-in on/off (off only once Google is linked, #149). Nothing else changes. */
export function useSetPasswordLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (password_login: boolean) => api.patch<User>("/auth/me", { password_login }),
    onSuccess: (user) => qc.setQueryData(keys.me, user),
  });
}

export function useUnlinkGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<User>("/auth/google"),
    onSuccess: (user) => qc.setQueryData(keys.me, user),
  });
}

/** Toggle "Ocultar cuentas individuales": every computed view changes, so refetch all. */
export function useUpdatePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: Pick<User, "hide_individual_accounts">) =>
      api.patch<User>("/auth/me", prefs),
    onSuccess: async (user) => {
      qc.setQueryData(keys.me, user);
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== keys.me[0] });
    },
  });
}
